import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { _getAdminViewsUsers } from '@/api/admin/queries';
import useDebounce from '@/global-hooks/use-debounce';
import { isValidEmail } from '@/utils/validate-email';

import { getCurrentProfileLookupMatch } from './get-current-profile-lookup-match';

/**
 * Looks up an existing Gamma Prime profile by email (debounced). Returns the
 * exactly-matching user, if any — used by the admin "add person" flows so a
 * manual entry links to the person's real account.
 */
export const useProfileLookup = (email: string, enabled: boolean) => {
  const normalizedEmail = email.trim().toLowerCase();
  const debouncedEmail = useDebounce(normalizedEmail, 400);
  const emailIsValid = isValidEmail(normalizedEmail);
  const debouncedEmailIsValid = isValidEmail(debouncedEmail);
  const debounceIsStale = normalizedEmail !== debouncedEmail;

  const { data, error, isError, isFetching, isPending, isSuccess, refetch } = useQuery({
    ..._getAdminViewsUsers({ search: debouncedEmail, limit: 5 }),
    enabled: enabled && debouncedEmailIsValid,
  });

  const queryMatchedUser = useMemo(
    () => data?.items.find((user) => user.email.toLowerCase() === debouncedEmail) ?? null,
    [data, debouncedEmail],
  );
  const matchedUser = getCurrentProfileLookupMatch(
    normalizedEmail,
    debouncedEmail,
    queryMatchedUser,
  );

  const lookupPending = enabled && emailIsValid && (debounceIsStale || isPending || isFetching);

  return {
    debouncedEmail,
    emailIsValid,
    error,
    isError: !debounceIsStale && isError,
    isFetching,
    isPending,
    isSuccess: !debounceIsStale && isSuccess,
    lookupPending,
    matchedUser,
    refetch,
  };
};
